"use strict";
/**
 * Shared locale-aware date formatting: dayjs locale mappings, date/time format
 * helpers, and relative-time formatting.
 *
 * Provides `fromNow` (human-readable relative timestamps),
 * `getDayjsLocaleFromIso`,`getLocaleDateFormat`, `getLocaleTimeOfDayFormat`,
 * `relativeTimeFormats`, and `normaliseLocale`.
 *
 * Accepts both full ISO locales (`'de-DE'`, `'fr-CH'`) and bare subtags
 * (`'de'`, `'fr'`), normalising internally via `normaliseLocale`.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.relativeTimeFormats = void 0;
exports.normaliseLocale = normaliseLocale;
exports.getDayjsLocaleFromIso = getDayjsLocaleFromIso;
exports.getLocaleDateFormat = getLocaleDateFormat;
exports.getLocaleTimeOfDayFormat = getLocaleTimeOfDayFormat;
exports.fromNow = fromNow;
const dayjs_1 = __importDefault(require("dayjs"));
const de_1 = __importDefault(require("dayjs/locale/de"));
const de_ch_1 = __importDefault(require("dayjs/locale/de-ch"));
const fr_1 = __importDefault(require("dayjs/locale/fr"));
const fr_ca_1 = __importDefault(require("dayjs/locale/fr-ca"));
const fr_ch_1 = __importDefault(require("dayjs/locale/fr-ch"));
const it_1 = __importDefault(require("dayjs/locale/it"));
const it_ch_1 = __importDefault(require("dayjs/locale/it-ch"));
const nl_1 = __importDefault(require("dayjs/locale/nl"));
dayjs_1.default.locale(de_1.default, undefined, true);
dayjs_1.default.locale(de_ch_1.default, undefined, true);
dayjs_1.default.locale(fr_1.default, undefined, true);
dayjs_1.default.locale(fr_ca_1.default, undefined, true);
dayjs_1.default.locale(fr_ch_1.default, undefined, true);
dayjs_1.default.locale(it_1.default, undefined, true);
dayjs_1.default.locale(it_ch_1.default, undefined, true);
dayjs_1.default.locale(nl_1.default, undefined, true);
// ---------------------------------------------------------------------------
// Locale normalisation
// ---------------------------------------------------------------------------
/**
 * Bare subtag → full ISO locale. When a caller passes `'de'` instead of
 * `'de-DE'`, this map picks a sensible default so the rest of the module
 * can work with a single key type.
 */
const bareToIso = {
    en: 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    nl: 'nl-NL',
    rm: 'rm-CH',
};
/** Normalises a bare subtag (`'de'`) or full locale (`'de-CH'`) to an ISO
 * locale string. */
function normaliseLocale(lang) {
    return bareToIso[lang.toLowerCase()] ?? lang;
}
const localeTable = {
    'en-US': { dayjsLocale: 'en' },
    'fr-FR': { dayjsLocale: 'fr' },
    'fr-CH': { dayjsLocale: 'fr-ch' },
    'fr-CA': { dayjsLocale: 'fr-ca' },
    'fr-BE': { dayjsLocale: 'fr' },
    'de-DE': { dayjsLocale: 'de' },
    'de-CH': { dayjsLocale: 'de-ch' },
    'it-IT': { dayjsLocale: 'it' },
    'it-CH': { dayjsLocale: 'it-ch' },
    'rm-CH': { dayjsLocale: 'de-ch' }, // no dayjs Romansh; fallback to German
    'nl-NL': { dayjsLocale: 'nl' },
};
/** Returns the dayjs locale string for an ISO locale code or bare subtag. */
function getDayjsLocaleFromIso(locale) {
    return localeTable[locale]?.dayjsLocale ?? localeTable[normaliseLocale(locale)]?.dayjsLocale ?? 'en';
}
// ---------------------------------------------------------------------------
// Date format helpers
// ---------------------------------------------------------------------------
/** Returns a dayjs date format string for the given ISO locale code. */
function getLocaleDateFormat(locale, { showYear } = { showYear: false }) {
    const resolved = normaliseLocale(locale);
    switch (resolved) {
        case 'en-US':
            return showYear ? 'MMM D, YYYY' : 'MMM D';
        default:
            return showYear ? 'D MMM YYYY' : 'D MMM';
    }
}
/** Returns a dayjs time-of-day format string for the given ISO locale code. */
function getLocaleTimeOfDayFormat(locale) {
    const resolved = normaliseLocale(locale);
    switch (resolved) {
        case 'en-US':
            return 'h:mma';
        case 'de-DE':
        case 'de-CH':
            return 'HH:mm [Uhr]';
        case 'fr-CA':
        case 'fr-FR':
        case 'fr-CH':
        case 'fr-BE':
            return 'H [h] mm';
        case 'it-IT':
        case 'it-CH':
            return 'HH:mm';
        default:
            return 'h:mma';
    }
}
exports.relativeTimeFormats = {
    en: { minute: '{n} minute ago', minutes: '{n} minutes ago', hour: '{n} hour ago', hours: '{n} hours ago' },
    fr: { minute: 'il y a {n} minute', minutes: 'il y a {n} minutes', hour: 'il y a {n} heure', hours: 'il y a {n} heures' },
    'fr-ca': { minute: 'il y a {n} minute', minutes: 'il y a {n} minutes', hour: 'il y a {n} heure', hours: 'il y a {n} heures' },
    'fr-ch': { minute: 'il y a {n} minute', minutes: 'il y a {n} minutes', hour: 'il y a {n} heure', hours: 'il y a {n} heures' },
    de: { minute: 'vor {n} Minute', minutes: 'vor {n} Minuten', hour: 'vor {n} Stunde', hours: 'vor {n} Stunden' },
    'de-ch': { minute: 'vor {n} Minute', minutes: 'vor {n} Minuten', hour: 'vor {n} Stunde', hours: 'vor {n} Stunden' },
    it: { minute: '{n} minuto fa', minutes: '{n} minuti fa', hour: '{n} ora fa', hours: '{n} ore fa' },
    'it-ch': { minute: '{n} minuto fa', minutes: '{n} minuti fa', hour: '{n} ora fa', hours: '{n} ore fa' },
    nl: { minute: '{n} minuut geleden', minutes: '{n} minuten geleden', hour: '{n} uur geleden', hours: '{n} uur geleden' },
};
/**
 * "Just now" per ISO locale. Sourced from human-translated i18n files so the
 * shared module matches the main client's output exactly.
 * **Not checked by the translation pipeline.**
 */
const justNowLabels = {
    'en-US': 'Just now',
    'de-DE': 'Gerade eben',
    'de-CH': 'Gerade eben',
    'fr-FR': 'À l\u2019instant',
    'fr-CH': 'À l\u2019instant',
    'fr-CA': 'À l\u2019instant',
    'fr-BE': 'À l\u2019instant',
    'it-IT': 'Proprio adesso',
    'it-CH': 'Proprio adesso',
    'nl-NL': 'Zojuist',
    'rm-CH': 'Gist ussa',
};
/**
 * "Yesterday at \{time\}" per ISO locale. `\{time\}` is replaced at runtime
 * with the locale-formatted time-of-day string.
 * **Not checked by the translation pipeline.**
 */
const yesterdayAtLabels = {
    'en-US': 'Yesterday at {time}',
    'de-DE': 'Gestern um {time}',
    'de-CH': 'Gestern um {time}',
    'fr-FR': 'Hier à {time}',
    'fr-CH': 'Hier à {time}',
    'fr-CA': 'Hier à {time}',
    'fr-BE': 'Hier à {time}',
    'it-IT': 'Ieri alle {time}',
    'it-CH': 'Ieri alle {time}',
    'nl-NL': 'Gisteren om {time}',
    'rm-CH': 'Ier a las {time}',
};
// ---------------------------------------------------------------------------
// Relative time formatting (internal)
// ---------------------------------------------------------------------------
/**
 * Formats relative time with strict numeric output (e.g., "1 minute ago" not
 * "a minute ago").
 */
function _formatDistanceStrict(inputDate, currentDate, dayjsLocale) {
    const diffMinutes = currentDate.diff(inputDate, 'minute');
    const diffHours = currentDate.diff(inputDate, 'hour');
    const formats = exports.relativeTimeFormats[dayjsLocale] || exports.relativeTimeFormats.en;
    if (diffHours < 1) {
        const template = diffMinutes === 1 ? formats.minute : formats.minutes;
        return template.replace('{n}', String(diffMinutes));
    }
    const template = diffHours === 1 ? formats.hour : formats.hours;
    return template.replace('{n}', String(diffHours));
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Formats a timestamp as a human-readable relative time string.
 *
 * - Under 60 s  → "Just now"  (localised)
 * - Same day    → "N minutes/hours ago"  (localised)
 * - Yesterday   → "Yesterday at HH:MM"  (localised)
 * - Same year   → "23 May"  (locale date format)
 * - Older       → "23 May 2024"  (locale date format with year)
 *
 * @param timestamp - Unix epoch milliseconds, ISO 8601 date string, or
 *                    `undefined`.
 * @param language  - ISO locale (`'de-DE'`) or bare subtag (`'de'`). Defaults
 *                    to `'en-US'`.
 */
// TODO: Once the v2 API ships ISO 8601 timestamps AND a database migration
// has converted all stored `number` timestamps to strings, streamline this
// signature to `string | undefined` and drop the number branch.
function fromNow(timestamp, language = 'en-US') {
    if (timestamp === undefined) {
        return '';
    }
    const ms = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    if (isNaN(ms)) {
        return '';
    }
    const isoLocale = normaliseLocale(language);
    const dayjsLocale = getDayjsLocaleFromIso(isoLocale);
    const currentDate = (0, dayjs_1.default)();
    const inputDate = (0, dayjs_1.default)(ms);
    const diffSeconds = currentDate.diff(inputDate, 'second');
    if (diffSeconds < 60) {
        return justNowLabels[isoLocale] || justNowLabels['en-US'];
    }
    const isSameDay = currentDate.isSame(inputDate, 'day');
    if (isSameDay) {
        return _formatDistanceStrict(inputDate, currentDate, dayjsLocale);
    }
    const diffCalendarDays = currentDate.startOf('day').diff(inputDate.startOf('day'), 'day');
    const isYesterday = diffCalendarDays === 1;
    if (isYesterday) {
        const time = inputDate.locale(dayjsLocale).format(getLocaleTimeOfDayFormat(isoLocale));
        const template = yesterdayAtLabels[isoLocale] || yesterdayAtLabels['en-US'];
        return template.replace('{time}', time);
    }
    const isSameYear = currentDate.isSame(inputDate, 'year');
    if (isSameYear) {
        return inputDate.locale(dayjsLocale).format(getLocaleDateFormat(isoLocale));
    }
    return inputDate.locale(dayjsLocale).format(getLocaleDateFormat(isoLocale, { showYear: true }));
}
//# sourceMappingURL=date.js.map